import { marketSessionDate } from './marketSessionDate';

const ET_CLOCK = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/New_York',
  hour12: false,
  hour: '2-digit',
  minute: '2-digit',
});

const iso = (date: Date): string => date.toISOString().slice(0, 10);
const utcDate = (year: number, month0: number, day: number): Date =>
  new Date(Date.UTC(year, month0, day, 12));

const observedFixedHoliday = (year: number, month0: number, day: number): string => {
  const date = utcDate(year, month0, day);
  const weekday = date.getUTCDay();
  if (weekday === 6) date.setUTCDate(date.getUTCDate() - 1);
  if (weekday === 0) date.setUTCDate(date.getUTCDate() + 1);
  return iso(date);
};

const nthWeekday = (year: number, month0: number, weekday: number, nth: number): string => {
  const first = utcDate(year, month0, 1);
  const offset = (weekday - first.getUTCDay() + 7) % 7;
  return iso(utcDate(year, month0, 1 + offset + (nth - 1) * 7));
};

const lastWeekday = (year: number, month0: number, weekday: number): string => {
  const last = utcDate(year, month0 + 1, 0);
  const offset = (last.getUTCDay() - weekday + 7) % 7;
  last.setUTCDate(last.getUTCDate() - offset);
  return iso(last);
};

/** Gregorian Easter Sunday (Meeus/Jones/Butcher), returned at UTC noon. */
const easterSunday = (year: number): Date => {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return utcDate(year, month - 1, day);
};

const holidayCache = new Map<number, Set<string>>();

const holidaysForYear = (year: number): Set<string> => {
  const cached = holidayCache.get(year);
  if (cached) return cached;
  const holidays = new Set<string>([
    nthWeekday(year, 0, 1, 3), // MLK Day
    nthWeekday(year, 1, 1, 3), // Presidents Day
    lastWeekday(year, 4, 1), // Memorial Day
    observedFixedHoliday(year, 6, 4),
    nthWeekday(year, 8, 1, 1), // Labor Day
    nthWeekday(year, 10, 4, 4), // Thanksgiving
    observedFixedHoliday(year, 11, 25),
  ]);
  // Unlike other fixed holidays, a Saturday New Year's Day is not observed on the prior
  // Friday: the exchange remains open on Dec 31 (NYSE's year-end accounting exception).
  // https://www.nyse.com/trade/hours-calendars
  if (utcDate(year, 0, 1).getUTCDay() !== 6) {
    holidays.add(observedFixedHoliday(year, 0, 1));
  }
  if (year >= 2022) holidays.add(observedFixedHoliday(year, 5, 19));
  const goodFriday = easterSunday(year);
  goodFriday.setUTCDate(goodFriday.getUTCDate() - 2);
  holidays.add(iso(goodFriday));
  holidayCache.set(year, holidays);
  return holidays;
};

/**
 * Closures the exchange has published, installed at runtime by `MarketCalendarProvider` from the
 * reference Worker (`/v1/reference/calendar`).
 *
 * THE RULES BELOW CANNOT DERIVE EVERY CLOSURE, AND THAT IS NOT A FIXABLE BUG.
 *
 * Fixed and floating holidays are arithmetic, so a computed calendar gets them right. Ad-hoc
 * closures are announcements: the market was shut on 2025-01-09 for Jimmy Carter, on 2018-12-05 for
 * George H.W. Bush, and for two days in 2012 for Hurricane Sandy. This module reported all four as
 * ordinary trading days, which on a live surface means a green dot and "every feed is live" on a
 * day nothing trades — the exact failure the status control exists to prevent.
 *
 * So the published calendar is the source of truth and these rules are the floor beneath it.
 *
 * The two are UNIONED, never overridden: a date is closed if either says so. That direction fails
 * safe. A holiday the provider omits still closes on our rules, and the worst an outage can do is
 * leave us exactly where we were before — never claiming a closed market is open.
 */
let publishedClosures: ReadonlySet<string> | null = null;

export const setPublishedMarketClosures = (dates: Iterable<string>): void => {
  const next = new Set(dates);
  // An empty list is an upstream failure, not a year without holidays. Keep what we had.
  if (next.size) publishedClosures = next;
};

/** Test seam only — restores the un-augmented computed calendar. */
export const clearPublishedMarketClosures = (): void => {
  publishedClosures = null;
};

/**
 * Early closes the exchange has published, installed at runtime from the reference calendar
 * (`/v1/reference/calendar`) as a { date → close-minute } map.
 *
 * The three recurring rules in `usEquityCloseMinutes` cover the scheduled half-days, but an ad-hoc
 * early close is an announcement no rule derives — the same gap the published *closures* fill for
 * full closures. This is the layer that fills it for half-days, now that the calendar source
 * publishes them (the previous FMP source did not, which is why early closes used to stay purely
 * computed).
 *
 * Unioned against the rules by the EARLIEST close winning: an installed half-day the rules can't
 * derive is honoured, and a stale or wrong entry can only ever understate a running session — the
 * safe direction for a control whose grey state reads as "nothing is updating".
 */
let publishedEarlyCloses: ReadonlyMap<string, number> | null = null;

export const setPublishedMarketEarlyCloses = (
  entries: Iterable<readonly [string, number]>,
): void => {
  const next = new Map<string, number>();
  for (const [date, closeMin] of entries) {
    // Guard the minute the same way the payload does — a malformed entry must not install a close
    // time that reopens or truncates a session.
    if (
      typeof date === 'string' &&
      Number.isInteger(closeMin) &&
      closeMin >= 0 &&
      closeMin < 24 * 60
    ) {
      next.set(date, closeMin);
    }
  }
  // An empty map is an upstream failure, not a calendar without half-days. Keep what we had; the
  // recurring rules still cover the scheduled early closes regardless.
  if (next.size) publishedEarlyCloses = next;
};

/** Test seam only — restores the purely computed early-close rules. */
export const clearPublishedMarketEarlyCloses = (): void => {
  publishedEarlyCloses = null;
};

export const isUsEquityTradingDate = (dateIso: string): boolean => {
  const date = new Date(`${dateIso}T12:00:00Z`);
  if (!Number.isFinite(date.getTime())) return false;
  const weekday = date.getUTCDay();
  if (weekday === 0 || weekday === 6) return false;
  if (publishedClosures?.has(dateIso)) return false;
  return !holidaysForYear(date.getUTCFullYear()).has(dateIso);
};

export const previousUsEquitySessionDate = (dateIso: string): string => {
  const cursor = new Date(`${dateIso}T12:00:00Z`);
  do cursor.setUTCDate(cursor.getUTCDate() - 1);
  while (!isUsEquityTradingDate(iso(cursor)));
  return iso(cursor);
};

/** The next scheduled US-equity session after `dateIso` (never the supplied date itself). */
export const nextUsEquitySessionDate = (dateIso: string): string => {
  const cursor = new Date(`${dateIso}T12:00:00Z`);
  do cursor.setUTCDate(cursor.getUTCDate() + 1);
  while (!isUsEquityTradingDate(iso(cursor)));
  return iso(cursor);
};

export const etMinutesOfDay = (nowMs: number): number => {
  const parts = ET_CLOCK.formatToParts(new Date(nowMs));
  const read = (type: string) => Number(parts.find((part) => part.type === type)?.value ?? NaN);
  // Some ICU builds format midnight as 24:xx even with hour12=false. It is still the beginning
  // of this market date, not its end; normalize before any session-completion decision.
  return (read('hour') % 24) * 60 + read('minute');
};

/** Regular US equity session; holidays are closed, not merely weekdays. */
export const isUsEquityRegularSession = (nowMs: number): boolean => {
  const today = marketSessionDate(nowMs);
  const minutes = etMinutesOfDay(nowMs);
  return (
    isUsEquityTradingDate(today) && minutes >= 9 * 60 + 30 && minutes < usEquityCloseMinutes(today)
  );
};

/**
 * The session a trader is preparing for at this moment. Before and during a cash session it is
 * today; once that session closes (or on a weekend/holiday), it advances to the next real exchange
 * session. This is deliberately calendar-aware so a Friday close never points at Saturday and an
 * observed holiday never masquerades as an open.
 */
export const usEquityPlanningSessionDate = (nowMs: number): string => {
  const today = marketSessionDate(nowMs);
  if (isUsEquityTradingDate(today) && etMinutesOfDay(nowMs) < usEquityCloseMinutes(today)) {
    return today;
  }
  return nextUsEquitySessionDate(today);
};

/**
 * Session represented by a chain/quote snapshot refreshed "now". Before the open and on market
 * holidays/weekends, the latest observation is the prior session even if a cache was just rewritten.
 */
export const marketDataSessionDate = (nowMs: number): string => {
  const today = marketSessionDate(nowMs);
  if (!isUsEquityTradingDate(today) || etMinutesOfDay(nowMs) < 9 * 60 + 30) {
    return previousUsEquitySessionDate(today);
  }
  return today;
};

/** The recurring 13:00 early-close rules — day after Thanksgiving, July 3, a weekday Dec 24. */
const computedCloseMinutes = (dateIso: string): number => {
  const date = new Date(`${dateIso}T12:00:00Z`);
  const month = date.getUTCMonth();
  const day = date.getUTCDate();
  const weekday = date.getUTCDay();
  const thanksgiving = nthWeekday(date.getUTCFullYear(), 10, 4, 4);
  const afterThanksgiving = new Date(`${thanksgiving}T12:00:00Z`);
  afterThanksgiving.setUTCDate(afterThanksgiving.getUTCDate() + 1);
  const commonEarlyClose =
    dateIso === iso(afterThanksgiving) ||
    (month === 6 && day === 3) ||
    (month === 11 && day === 24 && weekday >= 1 && weekday <= 5);
  return commonEarlyClose ? 13 * 60 : 16 * 60;
};

/**
 * Scheduled cash-session close in ET minutes.
 *
 * The recurring rules and any exchange-published early close are unioned by the EARLIEST close: a
 * published half-day the rules can't derive is honoured, and a stale or absent entry only ever
 * falls back to the rules — never claiming the tape runs later than it does.
 */
export const usEquityCloseMinutes = (dateIso: string): number => {
  if (!isUsEquityTradingDate(dateIso)) return 16 * 60;
  const computed = computedCloseMinutes(dateIso);
  const published = publishedEarlyCloses?.get(dateIso);
  return published === undefined ? computed : Math.min(computed, published);
};
